import {
    FeatureRollbackCursorPlacement,
    FeatureRollbackTimelineItemKind,
    type FeatureRollbackDatumTimelineInput,
    type FeatureRollbackFeatureTimelineInput,
    type FeatureRollbackReferenceTimelineSyncInput,
    type FeatureRollbackSketchTimelineInput,
    type FeatureRollbackProjectionState,
    type FeatureRollbackState,
    type FeatureRollbackTimelineItem,
} from './FeatureRollbackTypes';
import {
    createFeatureRollbackFeatureTimelineItem,
    createFeatureRollbackTimeline,
} from './FeatureRollbackTimelineBuilder';
import {featureCommandAdapterRegistry} from '../command/FeatureCommandAdapterRegistry';
import {
    createSketchIdentityIndex,
    normalizeSketchIdToRuntime,
} from '../../sketch/identity/sketchIdentityIndex';
import type {
    SketchFeaturePrefixResolution,
    SketchFeaturePrefixResolveInput,
} from '../../../types/feature/FeatureRollbackPrefix';

/**
 * 克隆命令序列，避免外部调用方持有内部时间线引用。
 *
 * @param commands 命令序列。
 * @returns 克隆后的命令序列。
 */
function cloneCommands(commands: readonly CadCommand[]): CadCommand[] {
    return commands.map((command) => structuredClone(command));
}

/**
 * 克隆 FeatureManager 退回序列，避免调用方持有内部引用。
 *
 * @param items FeatureManager 退回序列。
 * @returns 克隆后的退回序列。
 */
function cloneTimelineItems(items: readonly FeatureRollbackTimelineItem[]): FeatureRollbackTimelineItem[] {
    return items.map((item) => ({...item}));
}

/**
 * 创建 FeatureManager 时间线节点的稳定键。
 *
 * @param item FeatureManager 时间线节点。
 * @returns 稳定键。
 */
function createTimelineItemKey(item: FeatureRollbackTimelineItem): string {
    return `${item.kind}:${item.id}`;
}

/**
 * 按闭区间约束数字。
 *
 * @param value 输入值。
 * @param min 最小值。
 * @param max 最大值。
 * @returns 被约束后的值。
 */
function clampIndex(value: number, min: number, max: number): number {
    if (!Number.isFinite(value)) {
        return min;
    }
    return Math.max(min, Math.min(max, Math.trunc(value)));
}

/**
 * 解析特征命令对应的来源草图，并归一为场景运行时句柄。
 *
 * 设计说明：
 * 1. 特征命令与绑定记录的来源草图是持久 ID，而 FeatureManager 时间线的草图槽用运行时 uuid；
 * 2. 两者不换算就会判成两张草图，被吸收草图会重新占据顶级 Sketch 槽，与特征同级显示；
 * 3. 场景中不存在的草图（已删除或旧数据）保持原值，交由存活判定统一剪枝。
 *
 * @param command 特征命令。
 * @param sketchIdentityIndex 草图身份归一索引。
 * @returns 归一后的来源草图 ID；无来源草图时返回 null。
 */
function resolveCommandSourceSketchId(
    command: CadCommand,
    sketchIdentityIndex: ReadonlyMap<string, string>,
): string | null {
    const prototype = featureCommandAdapterRegistry.createPrototypeFromCommand(command);
    return normalizeSketchIdToRuntime(sketchIdentityIndex, prototype?.getSourceSketchId());
}

/**
 * 从场景草图摘要构建身份归一索引。
 *
 * @param sceneSketches 当前场景草图摘要。
 * @returns 草图身份归一索引。
 */
function createSceneSketchIdentityIndex(
    sceneSketches: readonly FeatureRollbackSketchTimelineInput[],
): Map<string, string> {
    return createSketchIdentityIndex(
        sceneSketches.map((sketch) => ({
            runtimeId: sketch.id,
            persistentId: sketch.persistentId,
        })),
    );
}

/**
 * FeatureManager 退回控制棒时间线控制器。
 *
 * 设计说明：
 * 1. Worker 只保存当前可重建的激活特征命令，控制器保存完整特征时间线；
 * 2. 设计树读取完整 FeatureManager 序列和 cursor，才能显示 SolidWorks 一样的灰显下游项目；
 * 3. 控制器不直接调用 Worker 或 Viewer，只负责纯时间线状态收口。
 */
export class FeatureRollbackController {
    /** 完整特征命令时间线。 */
    private fullCommands: CadCommand[] = [];
    /** 完整 FeatureManager 退回序列，包含特征、未吸收顶级草图与用户基准面/轴/点。 */
    private timelineItems: FeatureRollbackTimelineItem[] = [];
    /** 最近一次同步的用户基准面输入，供历史恢复重建完整 FeatureManager 序列。 */
    private lastSyncedDatumPlanes: readonly FeatureRollbackDatumTimelineInput[] = [];
    /** 最近一次同步的用户基准轴输入，供历史恢复重建完整 FeatureManager 序列。 */
    private lastSyncedDatumAxes: readonly FeatureRollbackDatumTimelineInput[] = [];
    /** 最近一次同步的用户基准点输入，供历史恢复重建完整 FeatureManager 序列。 */
    private lastSyncedDatumPoints: readonly FeatureRollbackDatumTimelineInput[] = [];
    /** 当前退回控制棒插入索引。 */
    private cursorIndex = 0;
    /** 上一次退回控制棒插入索引。 */
    private previousCursorIndex: number | null = null;
    /**
     * 从 Worker 当前命令历史同步完整时间线。
     *
     * 设计说明：
     * 1. 非退回状态下，Worker history 就是完整时间线；
     * 2. 退回状态下，如果 Worker history 仍等于当前前缀，说明只是 replay 退回位置；
     * 3. 退回状态下如果 Worker history 比当前前缀更长，说明用户在退回位置插入了新特征，
     *    此时新特征应插入到控制棒上方，而原下游特征、草图与用户基准继续保留在控制棒下方。
     *
     * @param workerCommands Worker 当前命令历史。
     * @returns 当前激活命令序列。
     */
    public reconcileWorkerHistory(workerCommands: readonly CadCommand[]): CadCommand[] {
        const nextWorkerCommands = cloneCommands(workerCommands);
        const activeFeatureCount = this.getActiveFeatureIds().length;

        /**
         * 不能用 fullCommands.length 判断是否初始化。
         * 只有顶级草图、没有建模特征时，完整命令时间线合法地为空，
         * 但 FeatureManager 退回序列仍包含草图，空 Worker replay 不应把 cursor 重置到底部。
         */
        if (!this.isRolledBack()) {
            this.fullCommands = nextWorkerCommands;
            this.rebuildFeatureOnlyTimeline();
            this.cursorIndex = this.timelineItems.length;
            this.previousCursorIndex = null;
            return this.getActiveCommands();
        }

        if (nextWorkerCommands.length === activeFeatureCount) {
            return this.getActiveCommands();
        }

        if (nextWorkerCommands.length > activeFeatureCount) {
            const insertedFeatureIds = new Set(
                nextWorkerCommands
                    .slice(activeFeatureCount)
                    .map((command) => command.id),
            );
            const upstreamItems = this.timelineItems.slice(0, this.cursorIndex);
            const insertedFeatureItems = nextWorkerCommands
                .slice(activeFeatureCount)
                .map((command) => createFeatureRollbackFeatureTimelineItem(command.id));
            const downstreamItems = this.timelineItems
                .slice(this.cursorIndex)
                .filter((item) => (
                    item.kind !== FeatureRollbackTimelineItemKind.Feature
                    || !insertedFeatureIds.has(item.id)
                ));
            const downstreamCommands = this.fullCommands
                .slice(activeFeatureCount)
                .filter((command) => !insertedFeatureIds.has(command.id));
            this.previousCursorIndex = this.cursorIndex;
            this.fullCommands = [
                ...nextWorkerCommands,
                ...downstreamCommands,
            ];
            this.timelineItems = [
                ...upstreamItems,
                ...insertedFeatureItems,
                ...downstreamItems,
            ];
            this.cursorIndex = upstreamItems.length + insertedFeatureItems.length;
            return this.getActiveCommands();
        }

        /**
         * 退回状态下 Worker history 只代表当前 active prefix，不再具备完整 FeatureManager
         * 权威语义。首个特征上方的合法 replay 会得到空 history；若这里把它提升为完整
         * history，就会清空 fullCommands，设计树随即表现为“特征消失、源草图被释放为顶层”。
         * 真正的外部完整历史恢复必须走 replaceTimeline / replaceTimelineForFeatureHistoryRestore。
         */
        return this.getActiveCommands();
    }

    /**
     * 用当前草图与用户基准几何同步完整 FeatureManager 退回序列。
     *
     * 设计说明：
     * 1. SolidWorks 的退回控制棒属于 FeatureManager 序列，不是单纯 feature command 序列；
     * 2. 被特征吸收的草图继续作为特征子节点，未被吸收的顶级草图与用户基准面/轴/点必须参与控制棒位置计算；
     * 3. 非退回状态同步新节点时，控制棒自动保持在树末尾，避免默认落在最后一个特征之后。
     *
     * @param sceneSketches 当前场景中的草图摘要。
     * @param referenceInput 用户基准面/轴/点摘要；缺省时清空基准几何时间线输入。
     */
    public syncFeatureManagerTimeline(
        sceneSketches: readonly FeatureRollbackSketchTimelineInput[],
        referenceInput: FeatureRollbackReferenceTimelineSyncInput = {},
    ): void {
        this.lastSyncedDatumPlanes = referenceInput.datumPlanes ?? [];
        this.lastSyncedDatumAxes = referenceInput.datumAxes ?? [];
        this.lastSyncedDatumPoints = referenceInput.datumPoints ?? [];

        /**
         * 退回态下不能整表按 order 重建：
         * 1. reconcileWorkerHistory 可能已在 cursor 处插入新特征；
         * 2. Undo 恢复的 timelineItems 可能保留棒下用户基准相对位置；
         * 3. 整表重建会把这两类语义冲掉，表现为“下游基准丢失/跑到棒上”。
         */
        if (this.isRolledBack()) {
            this.syncRolledBackFeatureManagerTimeline(sceneSketches);
            return;
        }

        this.timelineItems = createFeatureRollbackTimeline({
            features: this.createFeatureTimelineInputs(createSceneSketchIdentityIndex(sceneSketches)),
            sketches: sceneSketches,
            datumPlanes: this.lastSyncedDatumPlanes,
            datumAxes: this.lastSyncedDatumAxes,
            datumPoints: this.lastSyncedDatumPoints,
        });
        this.cursorIndex = this.timelineItems.length;
        this.previousCursorIndex = null;
    }

    /**
     * 用指定命令序列替换完整时间线。
     *
     * @param commands 新的完整命令序列。
     * @param cursorIndex 可选目标 cursor；默认位于末尾。
     * @param previousCursorIndex 可选上一 cursor；用于失败恢复时保留“退回到前”语义。
     * @param timelineItems 可选 FeatureManager 序列；用于失败恢复时保留顶级草图位置。
     */
    public replaceTimeline(
        commands: readonly CadCommand[],
        cursorIndex: number = commands.length,
        previousCursorIndex?: number | null,
        timelineItems?: readonly FeatureRollbackTimelineItem[],
    ): void {
        const oldCursorIndex = this.cursorIndex;
        this.fullCommands = cloneCommands(commands);
        this.timelineItems = timelineItems
            ? cloneTimelineItems(timelineItems)
            : this.fullCommands.map((command) => createFeatureRollbackFeatureTimelineItem(command.id));
        this.cursorIndex = clampIndex(cursorIndex, 0, this.timelineItems.length);
        this.previousCursorIndex = previousCursorIndex === undefined
            ? clampIndex(oldCursorIndex, 0, this.timelineItems.length)
            : previousCursorIndex === null
                ? null
                : clampIndex(previousCursorIndex, 0, this.timelineItems.length);
        if (!this.isRolledBack() && previousCursorIndex === undefined) {
            this.previousCursorIndex = null;
        }
    }

    /**
     * 在特征删除后替换完整时间线并保留退回控制棒语义位置。
     *
     * 设计说明：
     * 1. 删除控制棒上方特征时，控制棒应跟随剩余 FeatureManager 序列上移，而不是回到模型末尾；
     * 2. 删除控制棒下方特征时，控制棒上方序列不变，只需要对新长度做边界裁剪；
     * 3. 这里只根据旧 timeline 和被删除 featureId 计算 cursor，不涉及 Undo/Redo 历史事务。
     *
     * @param commands 删除后的完整命令序列。
     * @param deletedFeatureIds 本次删除移除的特征 ID。
     */
    public replaceTimelineAfterFeatureDeletion(
        commands: readonly CadCommand[],
        deletedFeatureIds: readonly string[],
    ): void {
        const nextCursorIndex = this.resolveCursorIndexAfterFeatureDeletion(this.cursorIndex, deletedFeatureIds);
        const nextPreviousCursorIndex = this.previousCursorIndex === null
            ? null
            : this.resolveCursorIndexAfterFeatureDeletion(this.previousCursorIndex, deletedFeatureIds);

        this.replaceTimeline(commands, nextCursorIndex, nextPreviousCursorIndex);
    }

    /**
     * 用 feature 历史恢复结果替换完整时间线，并保留退回控制棒上方的活动前缀语义。
     *
     * 设计说明：
     * 1. Undo/Redo 恢复的是完整 FeatureManager 主状态，不能只把 Worker 当前 active prefix 当成完整历史；
     * 2. 退回状态下撤销在控制棒上方插入的特征时，控制棒必须随活动前缀收缩，而不是固定旧索引；
     * 3. 重做这类特征时，如果新特征插入位置不晚于旧活动前缀末尾，则它应回到控制棒上方并参与 Worker replay。
     *
     * @param commands 恢复后的完整 feature 命令时间线。
     * @param sceneSketches 当前场景草图摘要，用于重建包含顶级草图的 FeatureManager 序列。
     * @param referenceInput 用户基准面/轴/点摘要；缺省时复用最近一次 sync 的基准输入。
     */
    public replaceTimelineForFeatureHistoryRestore(
        commands: readonly CadCommand[],
        sceneSketches: readonly FeatureRollbackSketchTimelineInput[],
        referenceInput?: FeatureRollbackReferenceTimelineSyncInput,
    ): void {
        const previousActiveItems = this.getActiveItems();
        const previousRolledBackItems = this.getRolledBackItems();
        const previousActiveFeatureCount = previousActiveItems
            .filter((item) => item.kind === FeatureRollbackTimelineItemKind.Feature)
            .length;
        const previousFeatureIds = new Set(this.fullCommands.map((command) => command.id));
        const activeItemKeys = new Set(previousActiveItems.map((item) => createTimelineItemKey(item)));
        const rolledBackItemKeys = new Set(previousRolledBackItems.map((item) => createTimelineItemKey(item)));
        const nextCommands = cloneCommands(commands);

        nextCommands.forEach((command, commandIndex) => {
            if (previousFeatureIds.has(command.id)) {
                return;
            }
            if (commandIndex <= previousActiveFeatureCount) {
                activeItemKeys.add(createTimelineItemKey(createFeatureRollbackFeatureTimelineItem(command.id)));
            }
        });

        if (referenceInput) {
            this.lastSyncedDatumPlanes = referenceInput.datumPlanes ?? [];
            this.lastSyncedDatumAxes = referenceInput.datumAxes ?? [];
            this.lastSyncedDatumPoints = referenceInput.datumPoints ?? [];
        }

        this.fullCommands = nextCommands;
        const orderedTimelineItems = createFeatureRollbackTimeline({
            features: this.createFeatureTimelineInputs(createSceneSketchIdentityIndex(sceneSketches)),
            sketches: sceneSketches,
            datumPlanes: this.lastSyncedDatumPlanes,
            datumAxes: this.lastSyncedDatumAxes,
            datumPoints: this.lastSyncedDatumPoints,
        });

        /**
         * 新建节点（恢复后首次出现）进入活动前缀；已在棒下的节点继续留在下游。
         * 最后按活动/下游分区拼回线性序列，保证控制棒仍是连续前缀切点。
         */
        orderedTimelineItems.forEach((item) => {
            const itemKey = createTimelineItemKey(item);
            if (!activeItemKeys.has(itemKey) && !rolledBackItemKeys.has(itemKey)) {
                activeItemKeys.add(itemKey);
            }
        });

        const partitioned = this.partitionTimelineItemsByActiveKeys(orderedTimelineItems, activeItemKeys);
        this.timelineItems = partitioned.timelineItems;
        this.cursorIndex = partitioned.cursorIndex;
        this.previousCursorIndex = null;
    }

    /**
     * 移动退回控制棒。
     *
     * @param cursorIndex 目标插入索引。
     */
    public moveCursor(cursorIndex: number): void {
        const nextCursorIndex = clampIndex(cursorIndex, 0, this.timelineItems.length);
        if (nextCursorIndex === this.cursorIndex) {
            return;
        }
        this.previousCursorIndex = this.cursorIndex;
        this.cursorIndex = nextCursorIndex;
    }

    /**
     * 将控制棒移动到目标特征相邻位置。
     *
     * @param featureId 目标特征 ID。
     * @param placement 控制棒相对目标特征的位置。
     * @returns 是否成功解析并移动。
     */
    public moveCursorToFeature(featureId: string, placement: FeatureRollbackCursorPlacement): boolean {
        const featureIndex = this.timelineItems.findIndex((item) => (
            item.kind === FeatureRollbackTimelineItemKind.Feature
            && item.id === featureId
        ));
        if (featureIndex < 0) {
            return false;
        }
        this.moveCursor(
            placement === FeatureRollbackCursorPlacement.BeforeFeature
                ? featureIndex
                : featureIndex + 1,
        );
        return true;
    }

    /**
     * 前滚一个 FeatureManager 节点。
     */
    public rollForwardOne(): void {
        this.moveCursor(this.cursorIndex + 1);
    }

    /**
     * 移动到上一次控制棒位置。
     *
     * @returns 是否存在可恢复的上一次位置。
     */
    public rollToPrevious(): boolean {
        if (this.previousCursorIndex === null) {
            return false;
        }
        const nextCursorIndex = this.previousCursorIndex;
        this.previousCursorIndex = this.cursorIndex;
        this.cursorIndex = clampIndex(nextCursorIndex, 0, this.timelineItems.length);
        return true;
    }

    /**
     * 移动到模型末尾。
     */
    public rollToEnd(): void {
        this.moveCursor(this.timelineItems.length);
    }

    /**
     * 读取当前激活命令前缀。
     *
     * @returns 激活命令序列。
     */
    public getActiveCommands(): CadCommand[] {
        const activeFeatureIdSet = new Set(this.getActiveFeatureIds());

        /**
         * FeatureManager 显示序列可能按草图吸收关系组织，但 Worker replay 必须遵守
         * 完整命令历史顺序，否则同一批激活特征会被错误重排。
         */
        return cloneCommands(this.fullCommands.filter((command) => activeFeatureIdSet.has(command.id)));
    }

    /**
     * 读取完整命令时间线。
     *
     * @returns 完整命令序列。
     */
    public getFullCommands(): CadCommand[] {
        return cloneCommands(this.fullCommands);
    }

    /**
     * 解析已有草图编辑使用的严格 Feature 命令前缀。
     *
     * @param input 目标草图的运行时与持久身份。
     * @returns 同一 Controller 快照产生的前缀计划，或明确失败原因。
     */
    public resolveSketchFeaturePrefix(
        input: SketchFeaturePrefixResolveInput,
    ): SketchFeaturePrefixResolution {
        /**
         * 计划中的命令、时间线和 cursor 必须来自同一时刻。
         * 这里先克隆完整快照，后续查询不再读取可变的 Controller 状态。
         */
        const fullCommands = cloneCommands(this.fullCommands);
        const timelineItems = cloneTimelineItems(this.timelineItems);
        const sourceCursorIndex = this.cursorIndex;

        /**
         * Feature 命令保存稳定草图 ID。完整命令数组本身就是权威求值顺序，
         * 因此首个命中的命令即为当前产品语义中的首个消费者。
         */
        const consumerCommandIndex = fullCommands.findIndex((command) => {
            const prototype = featureCommandAdapterRegistry.createPrototypeFromCommand(command);
            return prototype?.getSourceSketchId() === input.persistentSketchId;
        });

        /**
         * 未被 Feature 消费的草图才会作为顶级 Sketch 节点进入混合时间线。
         * 被吸收草图则由首个消费 Feature 节点代表其锚点。
         */
        const topLevelSketchTimelineIndex = timelineItems.findIndex((item) => (
            item.kind === FeatureRollbackTimelineItemKind.Sketch
            && item.id === input.runtimeSketchId
        ));

        if (consumerCommandIndex >= 0 && topLevelSketchTimelineIndex >= 0) {
            return {
                success: false,
                reason: 'timeline-inconsistent',
                message: `目标草图同时被 Feature 消费并作为顶级草图存在：${input.runtimeSketchId}`,
            };
        }

        if (consumerCommandIndex < 0 && topLevelSketchTimelineIndex < 0) {
            return {
                success: false,
                reason: 'sketch-not-found',
                message: `未在完整命令或 FeatureManager 时间线中找到目标草图：${input.runtimeSketchId}`,
            };
        }

        let anchorTimelineIndex: number;
        let cutoffFeatureIndex: number;
        let inactiveTimelineStartIndex: number;
        if (consumerCommandIndex >= 0) {
            const resolvedConsumerCommand = fullCommands[consumerCommandIndex];
            if (!resolvedConsumerCommand) {
                return {
                    success: false,
                    reason: 'timeline-inconsistent',
                    message: `无法读取消费 Feature 命令：${consumerCommandIndex}`,
                };
            }

            anchorTimelineIndex = timelineItems.findIndex((item) => (
                item.kind === FeatureRollbackTimelineItemKind.Feature
                && item.id === resolvedConsumerCommand.id
            ));
            if (anchorTimelineIndex < 0) {
                return {
                    success: false,
                    reason: 'timeline-inconsistent',
                    message: `消费 Feature 不在 FeatureManager 时间线中：${resolvedConsumerCommand.id}`,
                };
            }

            /**
             * 只校验本次查询真正依赖的局部换算关系，不在每次查询时重复审核
             * Controller 与 TimelineBuilder 已经维护的整条 Feature 顺序。
             */
            const featureCountBeforeAnchor = timelineItems
                .slice(0, anchorTimelineIndex)
                .filter((item) => item.kind === FeatureRollbackTimelineItemKind.Feature)
                .length;
            if (featureCountBeforeAnchor !== consumerCommandIndex) {
                return {
                    success: false,
                    reason: 'timeline-inconsistent',
                    message: `消费 Feature 的时间线位置与命令位置不一致：${resolvedConsumerCommand.id}`,
                };
            }

            cutoffFeatureIndex = consumerCommandIndex;
            inactiveTimelineStartIndex = anchorTimelineIndex;
        } else {
            anchorTimelineIndex = topLevelSketchTimelineIndex;
            cutoffFeatureIndex = timelineItems
                .slice(0, anchorTimelineIndex)
                .filter((item) => item.kind === FeatureRollbackTimelineItemKind.Feature)
                .length;

            /**
             * 未被消费的目标草图仍处于当前编辑状态，inactive 后缀必须从其后开始。
             */
            inactiveTimelineStartIndex = anchorTimelineIndex + 1;
        }

        /**
         * cursor 是 FeatureManager 序列的插入索引；锚点位于 cursor 起始位置或其后，
         * 表示目标已经处于正式 Rollback Bar 下方，不能再创建临时编辑前缀。
         */
        if (anchorTimelineIndex >= sourceCursorIndex) {
            return {
                success: false,
                reason: 'sketch-rolled-back',
                message: `目标草图位于正式 Rollback Bar 下方：${input.runtimeSketchId}`,
            };
        }

        return {
            success: true,
            plan: {
                fullCommands,
                cutoffFeatureIndex,
                inactiveTimelineItems: timelineItems.slice(inactiveTimelineStartIndex),
                sourceCursorIndex,
            },
        };
    }

    /**
     * 判断特征是否处于退回状态。
     *
     * @param featureId 特征 ID。
     * @returns 是否在控制棒下方。
     */
    public isFeatureRolledBack(featureId: string): boolean {
        return this.getRolledBackItems()
            .some((item) => item.kind === FeatureRollbackTimelineItemKind.Feature && item.id === featureId);
    }

    /**
     * 判断顶级草图是否处于退回状态。
     *
     * @param sketchId 草图 ID。
     * @returns 是否在控制棒下方。
     */
    public isSketchRolledBack(sketchId: string): boolean {
        return this.getRolledBackItems()
            .some((item) => item.kind === FeatureRollbackTimelineItemKind.Sketch && item.id === sketchId);
    }

    /**
     * 判断用户基准面是否处于退回状态。
     *
     * @param planeId 基准面 ID（字符串）。
     * @returns 是否在控制棒下方。
     */
    public isDatumPlaneRolledBack(planeId: string): boolean {
        return this.getRolledBackItems()
            .some((item) => item.kind === FeatureRollbackTimelineItemKind.DatumPlane && item.id === planeId);
    }

    /**
     * 判断用户基准轴是否处于退回状态。
     *
     * @param axisId 基准轴 ID（字符串）。
     * @returns 是否在控制棒下方。
     */
    public isDatumAxisRolledBack(axisId: string): boolean {
        return this.getRolledBackItems()
            .some((item) => item.kind === FeatureRollbackTimelineItemKind.DatumAxis && item.id === axisId);
    }

    /**
     * 判断用户基准点是否处于退回状态。
     *
     * @param pointId 基准点 ID（字符串）。
     * @returns 是否在控制棒下方。
     */
    public isDatumPointRolledBack(pointId: string): boolean {
        return this.getRolledBackItems()
            .some((item) => item.kind === FeatureRollbackTimelineItemKind.DatumPoint && item.id === pointId);
    }

    /**
     * 读取完整退回控制棒状态。
     *
     * @returns 当前状态。
     */
    public getState(): FeatureRollbackState {
        const featureIds = this.fullCommands.map((command) => command.id);
        const activeFeatureIds = this.getActiveFeatureIds();
        const rolledBackFeatureIds = this.getRolledBackFeatureIds();
        const rolledBackSketchIds = this.getRolledBackSketchIds();
        const rolledBackDatumPlaneIds = this.getRolledBackDatumPlaneIds();
        const rolledBackDatumAxisIds = this.getRolledBackDatumAxisIds();
        const rolledBackDatumPointIds = this.getRolledBackDatumPointIds();

        return {
            cursorIndex: this.cursorIndex,
            previousCursorIndex: this.previousCursorIndex,
            totalFeatureCount: this.fullCommands.length,
            totalTimelineItemCount: this.timelineItems.length,
            isRolledBack: this.isRolledBack(),
            timelineItems: cloneTimelineItems(this.timelineItems),
            featureIds,
            activeFeatureIds,
            rolledBackFeatureIds,
            rolledBackSketchIds,
            rolledBackDatumPlaneIds,
            rolledBackDatumAxisIds,
            rolledBackDatumPointIds,
        };
    }

    /**
     * 读取设计树投影状态。
     *
     * @returns 投影状态。
     */
    public getProjectionState(): FeatureRollbackProjectionState {
        const state = this.getState();

        return {
            cursorIndex: state.cursorIndex,
            totalFeatureCount: state.totalFeatureCount,
            totalTimelineItemCount: state.totalTimelineItemCount,
            timelineItems: state.timelineItems,
            rolledBackFeatureIds: state.rolledBackFeatureIds,
            rolledBackSketchIds: state.rolledBackSketchIds,
            rolledBackDatumPlaneIds: state.rolledBackDatumPlaneIds,
            rolledBackDatumAxisIds: state.rolledBackDatumAxisIds,
            rolledBackDatumPointIds: state.rolledBackDatumPointIds,
        };
    }

    /**
     * 读取控制棒上方的 FeatureManager 序列节点。
     *
     * @returns 控制棒上方节点。
     */
    private getActiveItems(): FeatureRollbackTimelineItem[] {
        return this.timelineItems.slice(0, this.cursorIndex);
    }

    /**
     * 读取控制棒下方的 FeatureManager 序列节点。
     *
     * @returns 控制棒下方节点。
     */
    private getRolledBackItems(): FeatureRollbackTimelineItem[] {
        return this.timelineItems.slice(this.cursorIndex);
    }

    /**
     * 读取控制棒上方的特征 ID。
     *
     * @returns 激活特征 ID 序列。
     */
    private getActiveFeatureIds(): string[] {
        return this.getActiveItems()
            .filter((item) => item.kind === FeatureRollbackTimelineItemKind.Feature)
            .map((item) => item.id);
    }

    /**
     * 读取控制棒下方的特征 ID。
     *
     * @returns 退回特征 ID 序列。
     */
    private getRolledBackFeatureIds(): string[] {
        return this.getRolledBackItems()
            .filter((item) => item.kind === FeatureRollbackTimelineItemKind.Feature)
            .map((item) => item.id);
    }

    /**
     * 读取控制棒下方的顶级草图 ID。
     *
     * @returns 退回草图 ID 序列。
     */
    private getRolledBackSketchIds(): string[] {
        return this.getRolledBackItems()
            .filter((item) => item.kind === FeatureRollbackTimelineItemKind.Sketch)
            .map((item) => item.id);
    }

    /**
     * 读取控制棒下方的用户基准面 ID。
     *
     * @returns 退回基准面 ID 序列。
     */
    private getRolledBackDatumPlaneIds(): string[] {
        return this.getRolledBackItems()
            .filter((item) => item.kind === FeatureRollbackTimelineItemKind.DatumPlane)
            .map((item) => item.id);
    }

    /**
     * 读取控制棒下方的用户基准轴 ID。
     *
     * @returns 退回基准轴 ID 序列。
     */
    private getRolledBackDatumAxisIds(): string[] {
        return this.getRolledBackItems()
            .filter((item) => item.kind === FeatureRollbackTimelineItemKind.DatumAxis)
            .map((item) => item.id);
    }

    /**
     * 读取控制棒下方的用户基准点 ID。
     *
     * @returns 退回基准点 ID 序列。
     */
    private getRolledBackDatumPointIds(): string[] {
        return this.getRolledBackItems()
            .filter((item) => item.kind === FeatureRollbackTimelineItemKind.DatumPoint)
            .map((item) => item.id);
    }

    /**
     * 在退回态下增量同步 FeatureManager 序列。
     *
     * 设计说明：
     * 1. 保留现有节点相对顺序与 cursor，避免冲掉插入特征或 Undo 恢复的棒下基准；
     * 2. 删除场景中已不存在或被特征吸收的节点，并回缩 cursor；
     * 3. Sketch 存活判定与投影层吸收语义共用 `sourceSketchId`：已吸收草图不得再占顶级 Sketch 槽；
     * 4. Hidden 显隐不是死亡条件，禁止因退回旁路隐藏而剪掉草图槽；
     * 5. 新增草图 / 用户基准 / 尚未入序列的特征插到控制棒上方，对齐 SolidWorks “退回中创建落在棒上”。
     *
     * @param sceneSketches 当前场景草图摘要。
     */
    private syncRolledBackFeatureManagerTimeline(
        sceneSketches: readonly FeatureRollbackSketchTimelineInput[],
    ): void {
        const featureIds = new Set(this.fullCommands.map((command) => command.id));
        /**
         * 与 FeatureTreeProjectionBuilder 中 `consumedByFeatureId` 同源：
         * 完整 feature command 的 sourceSketchId 一旦存在，该草图只由 Feature 槽代表。
         */
        const sketchIdentityIndex = createSceneSketchIdentityIndex(sceneSketches);
        const absorbedSketchIds = this.collectAbsorbedSourceSketchIds(sketchIdentityIndex);
        const freeSketchIds = new Set(
            sceneSketches
                .map((sketch) => sketch.id)
                .filter((sketchId) => !absorbedSketchIds.has(sketchId)),
        );
        const planeIds = new Set(this.lastSyncedDatumPlanes.map((plane) => plane.id));
        const axisIds = new Set(this.lastSyncedDatumAxes.map((axis) => axis.id));
        const pointIds = new Set(this.lastSyncedDatumPoints.map((point) => point.id));

        const isTimelineItemAlive = (item: FeatureRollbackTimelineItem): boolean => {
            switch (item.kind) {
                case FeatureRollbackTimelineItemKind.Feature:
                    return featureIds.has(item.id);
                case FeatureRollbackTimelineItemKind.Sketch:
                    /**
                     * 顶级 Sketch 槽仅在场景仍存在且未被特征吸收时存活。
                     * Hidden 不参与判定；场景组已注销或已被 sourceSketchId 吸收时剪枝。
                     */
                    return freeSketchIds.has(item.id);
                case FeatureRollbackTimelineItemKind.DatumPlane:
                    return planeIds.has(item.id);
                case FeatureRollbackTimelineItemKind.DatumAxis:
                    return axisIds.has(item.id);
                case FeatureRollbackTimelineItemKind.DatumPoint:
                    return pointIds.has(item.id);
                default:
                    return false;
            }
        };

        let nextCursorIndex = this.cursorIndex;
        const retainedItems: FeatureRollbackTimelineItem[] = [];
        this.timelineItems.forEach((item, itemIndex) => {
            if (!isTimelineItemAlive(item)) {
                if (itemIndex < nextCursorIndex) {
                    nextCursorIndex -= 1;
                }
                return;
            }
            retainedItems.push(item);
        });

        const presentItemKeys = new Set(retainedItems.map((item) => createTimelineItemKey(item)));
        const insertedItems: FeatureRollbackTimelineItem[] = [];

        const tryInsertTimelineItem = (item: FeatureRollbackTimelineItem): void => {
            const itemKey = createTimelineItemKey(item);
            if (presentItemKeys.has(itemKey)) {
                return;
            }
            insertedItems.push(item);
            presentItemKeys.add(itemKey);
        };

        this.fullCommands.forEach((command) => {
            tryInsertTimelineItem(createFeatureRollbackFeatureTimelineItem(command.id));
        });
        freeSketchIds.forEach((sketchId) => {
            tryInsertTimelineItem({
                kind: FeatureRollbackTimelineItemKind.Sketch,
                id: sketchId,
            });
        });
        this.lastSyncedDatumPlanes.forEach((plane) => {
            tryInsertTimelineItem({
                kind: FeatureRollbackTimelineItemKind.DatumPlane,
                id: plane.id,
            });
        });
        this.lastSyncedDatumAxes.forEach((axis) => {
            tryInsertTimelineItem({
                kind: FeatureRollbackTimelineItemKind.DatumAxis,
                id: axis.id,
            });
        });
        this.lastSyncedDatumPoints.forEach((point) => {
            tryInsertTimelineItem({
                kind: FeatureRollbackTimelineItemKind.DatumPoint,
                id: point.id,
            });
        });

        nextCursorIndex = clampIndex(nextCursorIndex, 0, retainedItems.length);
        this.timelineItems = [
            ...retainedItems.slice(0, nextCursorIndex),
            ...insertedItems,
            ...retainedItems.slice(nextCursorIndex),
        ];
        this.cursorIndex = clampIndex(
            nextCursorIndex + insertedItems.length,
            0,
            this.timelineItems.length,
        );
        this.previousCursorIndex = this.previousCursorIndex === null
            ? null
            : clampIndex(this.previousCursorIndex, 0, this.timelineItems.length);
    }

    /**
     * 收集完整特征命令中已被吸收的来源草图 ID。
     *
     * @param sketchIdentityIndex 草图身份归一索引。
     * @returns 吸收草图 ID 集合（已归一为场景运行时句柄）。
     */
    private collectAbsorbedSourceSketchIds(
        sketchIdentityIndex: ReadonlyMap<string, string>,
    ): Set<string> {
        return new Set(
            this.createFeatureTimelineInputs(sketchIdentityIndex)
                .map((feature) => feature.sourceSketchId)
                .filter((sketchId): sketchId is string => typeof sketchId === 'string' && sketchId.length > 0),
        );
    }

    /**
     * 按活动键把 FeatureManager 序列分区为“棒上连续前缀 + 棒下剩余”。
     *
     * @param timelineItems 候选时间线。
     * @param activeItemKeys 应位于控制棒上方的节点键。
     * @returns 分区后的时间线与 cursor。
     */
    private partitionTimelineItemsByActiveKeys(
        timelineItems: readonly FeatureRollbackTimelineItem[],
        activeItemKeys: ReadonlySet<string>,
    ): {
        timelineItems: FeatureRollbackTimelineItem[];
        cursorIndex: number;
    } {
        const activeItems: FeatureRollbackTimelineItem[] = [];
        const rolledBackItems: FeatureRollbackTimelineItem[] = [];
        timelineItems.forEach((item) => {
            if (activeItemKeys.has(createTimelineItemKey(item))) {
                activeItems.push(item);
                return;
            }
            rolledBackItems.push(item);
        });
        return {
            timelineItems: [...activeItems, ...rolledBackItems],
            cursorIndex: activeItems.length,
        };
    }

    /**
     * 重建仅包含特征的默认退回序列。
     */
    private rebuildFeatureOnlyTimeline(): void {
        this.timelineItems = this.fullCommands.map((command) => createFeatureRollbackFeatureTimelineItem(command.id));
    }

    /**
     * 创建特征时间线输入。
     *
     * 设计说明：
     * 1. `order` 优先使用命令创建时间戳，便于与草图/基准几何共用同一混排刻度；
     * 2. 缺失时间戳时回退到命令索引，保证旧数据与单测仍可排序；
     * 3. 吸收草图占位仍由 TimelineBuilder 改用草图 order，不强行覆盖该语义。
     *
     * @param sketchIdentityIndex 草图身份归一索引。
     * @returns 特征时间线输入集合。
     */
    private createFeatureTimelineInputs(
        sketchIdentityIndex: ReadonlyMap<string, string>,
    ): FeatureRollbackFeatureTimelineInput[] {
        return this.fullCommands.map((command, commandIndex) => ({
            id: command.id,
            order: Number.isFinite(command.timestamp) ? Number(command.timestamp) : commandIndex,
            sourceSketchId: resolveCommandSourceSketchId(command, sketchIdentityIndex),
        }));
    }

    /**
     * 将旧 cursor 映射到删除后的新 FeatureManager 序列。
     *
     * @param cursorIndex 旧 cursor。
     * @param deletedFeatureIds 删除的特征 ID。
     * @returns 删除后的 cursor。
     */
    private resolveCursorIndexAfterFeatureDeletion(
        cursorIndex: number,
        deletedFeatureIds: readonly string[],
    ): number {
        const deletedFeatureIdSet = new Set(deletedFeatureIds);
        const deletedBeforeCursorCount = this.timelineItems
            .slice(0, clampIndex(cursorIndex, 0, this.timelineItems.length))
            .filter((item) => item.kind === FeatureRollbackTimelineItemKind.Feature && deletedFeatureIdSet.has(item.id))
            .length;

        return cursorIndex - deletedBeforeCursorCount;
    }

    /**
     * 判断当前是否处于退回状态。
     *
     * @returns 是否存在控制棒下方的 FeatureManager 节点。
     */
    private isRolledBack(): boolean {
        return this.cursorIndex < this.timelineItems.length;
    }
}
